import {
  AutoPayStatus,
  EmployeePaymentMethodType,
  EmployeeRole,
  EmploymentStatus,
  EmploymentType,
  EnrollmentStatus,
  FamilyUnit,
  InitialPremiumPaymentStatus,
  OnboardingType,
  PlanMarket,
  PolicyType,
  QualifyingLifeEventType,
  SelfEnrollType,
  SubmissionType,
  USState,
  UserStatus,
} from '@zorro/clients';
import type {
  EmployeesReportFilters,
  EmployeesReportRowDto,
} from '@zorro/clients';

import type { Filter } from '../Filters/FilterUtils';

type EmployeesFilterColumn = keyof EmployeesReportFilters;
type FilterOptionValues = string[] | undefined;

const EMPTY_OPTION_VALUE = 'null';
const INTEGER_OPTION_PATTERN = /^\d+$/u;
const BOOLEAN_OPTION_VALUES = new Map([
  ['true', true],
  ['false', false],
]);

const findFilterOptionValues = (
  filters: Filter<EmployeesReportRowDto>[],
  column: EmployeesFilterColumn
): FilterOptionValues => {
  const value = filters.find(({ type }) => type === column)?.value;

  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : undefined;
};

const findEnumMember = <TEnum extends string>(
  value: string,
  enumObject: Record<string, TEnum>
): TEnum | undefined =>
  Object.values(enumObject).find((member) => member === value);

const toEnumFilterValues = <TEnum extends string>(
  values: FilterOptionValues,
  enumObject: Record<string, TEnum>
): TEnum[] | undefined =>
  values?.flatMap((value) => findEnumMember(value, enumObject) ?? []);

const toNullableEnumFilterValues = <TEnum extends string>(
  values: FilterOptionValues,
  enumObject: Record<string, TEnum>
): (TEnum | null)[] | undefined =>
  values?.flatMap((value) =>
    value === EMPTY_OPTION_VALUE
      ? [null]
      : (findEnumMember(value, enumObject) ?? [])
  );

const toNullableStringFilterValues = (
  values: FilterOptionValues
): (string | null)[] | undefined =>
  values?.map((value) => (value === EMPTY_OPTION_VALUE ? null : value));

const toNullableIntegerFilterValues = (
  values: FilterOptionValues
): (number | null)[] | undefined =>
  values?.flatMap((value) => {
    if (value === EMPTY_OPTION_VALUE) {
      return [null];
    }
    return INTEGER_OPTION_PATTERN.test(value) ? [Number(value)] : [];
  });

const toNullableBooleanFilterValues = (
  values: FilterOptionValues
): (boolean | null)[] | undefined =>
  values?.flatMap((value) =>
    value === EMPTY_OPTION_VALUE
      ? [null]
      : (BOOLEAN_OPTION_VALUES.get(value) ?? [])
  );

export const toEmployeesReportFilters = (
  filters: Filter<EmployeesReportRowDto>[]
): EmployeesReportFilters => {
  const findColumnValues = (column: EmployeesFilterColumn) =>
    findFilterOptionValues(filters, column);

  const reportFilters: {
    [
      key in keyof Required<EmployeesReportFilters>
    ]: EmployeesReportFilters[key];
  } = {
    invitationStatus: toEnumFilterValues(
      findColumnValues('invitationStatus'),
      UserStatus
    ),
    employeeRole: toEnumFilterValues(
      findColumnValues('employeeRole'),
      EmployeeRole
    ),
    employmentType: toNullableEnumFilterValues(
      findColumnValues('employmentType'),
      EmploymentType
    ),
    state: toNullableEnumFilterValues(findColumnValues('state'), USState),
    class: toNullableStringFilterValues(findColumnValues('class')),
    benefitEligibility: toNullableEnumFilterValues(
      findColumnValues('benefitEligibility'),
      EmploymentStatus
    ),
    enrollmentStatus: toEnumFilterValues(
      findColumnValues('enrollmentStatus'),
      EnrollmentStatus
    ),
    enrollmentType: toNullableEnumFilterValues(
      findColumnValues('enrollmentType'),
      OnboardingType
    ),
    qleType: toNullableEnumFilterValues(
      findColumnValues('qleType'),
      QualifyingLifeEventType
    ),
    enrollmentSubmissionType: toNullableEnumFilterValues(
      findColumnValues('enrollmentSubmissionType'),
      SubmissionType
    ),
    medicalPlanType: toNullableEnumFilterValues(
      findColumnValues('medicalPlanType'),
      PolicyType
    ),
    medicalSelfEnroll: toNullableEnumFilterValues(
      findColumnValues('medicalSelfEnroll'),
      SelfEnrollType
    ),
    paymentMethodType: toNullableEnumFilterValues(
      findColumnValues('paymentMethodType'),
      EmployeePaymentMethodType
    ),
    initialPremiumPaymentStatus: toNullableEnumFilterValues(
      findColumnValues('initialPremiumPaymentStatus'),
      InitialPremiumPaymentStatus
    ),
    autoPayStatus: toNullableEnumFilterValues(
      findColumnValues('autoPayStatus'),
      AutoPayStatus
    ),
    medicalCarrier: toNullableStringFilterValues(
      findColumnValues('medicalCarrier')
    ),
    medicalPlanMarket: toNullableEnumFilterValues(
      findColumnValues('medicalPlanMarket'),
      PlanMarket
    ),
    benefitFamilyUnit: toNullableEnumFilterValues(
      findColumnValues('benefitFamilyUnit'),
      FamilyUnit
    ),
    dependentCount: toNullableIntegerFilterValues(
      findColumnValues('dependentCount')
    ),
    medicalIsHsaEligible: toNullableBooleanFilterValues(
      findColumnValues('medicalIsHsaEligible')
    ),
    enrollmentTeamId: toNullableStringFilterValues(
      findColumnValues('enrollmentTeamId')
    ),
    enrollmentAgentEmail: toNullableStringFilterValues(
      findColumnValues('enrollmentAgentEmail')
    ),
    subEntityName: toNullableStringFilterValues(
      findColumnValues('subEntityName')
    ),
    subEntityName2: toNullableStringFilterValues(
      findColumnValues('subEntityName2')
    ),
  };

  return reportFilters;
};
